import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface CompletionModalProps {
  /** 完了したジョブの結果ファイル名 (見出しに表示)。 */
  fileName: string;
  /** [結果を開く] 押下。 結果カードへ反映する。 */
  onOpenResult: () => void;
  /** [閉じる] 押下 / Escape / backdrop クリック。 反映せず閉じる (履歴へフォールバック)。 */
  onClose: () => void;
}

/**
 * issue #85 round 2: 自動保存 (fire-and-forget) ジョブ完了時の完了モーダル。
 * 無言の即時自動復元をやめ、「『<譜面名>』の運指が完成しました」+ [結果を開く] [閉じる] を出す。
 * DiscardConfirmModal の作法 (createPortal / Escape / backdrop+stopPropagation / role=dialog) を踏襲。
 */
const CompletionModal: React.FC<CompletionModalProps> = ({ fileName, onOpenResult, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      data-testid="completion-modal-backdrop"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fadein"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="completion-modal-title"
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl p-6 w-[90vw] max-w-md animate-popin"
      >
        <div className="flex flex-col items-center text-center mb-5">
          <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mb-3">
            <svg
              className="w-6 h-6 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 id="completion-modal-title" className="text-lg font-bold text-gray-800">
            運指が完成しました
          </h2>
          <p className="mt-1.5 text-sm text-gray-500 break-all">「{fileName}」</p>
        </div>
        <div className="flex flex-col gap-2.5">
          <button
            type="button"
            onClick={onOpenResult}
            className="w-full px-4 py-2.5 rounded-lg text-sm font-bold text-white bg-green-600 hover:bg-green-700 shadow-sm transition-colors"
          >
            結果を開く
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CompletionModal;
